import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DateCell, NameCell } from '@/components/TableCells'
import { DeliveryStatusBadge, deliveryStatusOf } from '@/components/DeliveryStatusBadge'
import { CampaignTimeline, PacingBadge, flightStateOf, pacingOf, type Pacing } from '@/components/CampaignTimeline'
import { AttentionCard, type AttentionTab } from '@/components/AttentionCard'
import { useNetwork } from '../contexts/NetworkContext'
import { getContext } from '@/services/networks/networksApi'
import { AuthService } from '@/services/core/auth'
import { LineItemsApi, type LineItem } from '@/services/lineItems/lineItemsApi'

/** How far ahead "ending soon" looks. */
const ENDING_SOON_DAYS = 7
const DAY_MS = 24 * 60 * 60 * 1000

/** A line item with its pacing worked out once, for sorting and for the cells. */
interface AttentionRow extends LineItem {
  pacing: Pacing
}

function withPacing(li: LineItem): AttentionRow {
  const flight = flightStateOf(li.startDate, li.endDate, li.executionState, li.elapsedTimePercentage ?? null)
  return { ...li, pacing: pacingOf(flight, li.deliveryPercentage ?? null) }
}

function isUnderDelivering(row: AttentionRow) {
  return row.pacing.status === 'behind' || row.pacing.status === 'farBehind'
}

function endsWithin(li: LineItem, days: number) {
  if (!li.endDate) return false
  const msLeft = new Date(li.endDate).getTime() - Date.now()
  return msLeft >= 0 && msLeft <= days * DAY_MS
}

function hasNotEnded(li: LineItem) {
  return !li.endDate || new Date(li.endDate).getTime() > Date.now()
}

/**
 * The line items someone should look at today: running ones behind pace, ones
 * the ad server cannot deliver, ones about to end, and ones waiting on an
 * approval.
 */
export function LineItemAttentionCard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { scopeKey, scopeReady } = useNetwork()
  const enabled = AuthService.isAuthenticated() && scopeReady
  const context = getContext()

  // Under-delivery and ending-soon both come out of the running line items.
  // Sorted by end date, so if a very large network hits the page cap it is the
  // campaigns furthest from ending that are left out.
  const running = useQuery({
    queryKey: ['lineitems', 'attention', 'running', context, scopeKey],
    queryFn: () =>
      LineItemsApi.queryAll({ where: ['lineItem.executionState in RUNNING'], orderBy: 'endDate' }),
    enabled,
  })

  const notReady = useQuery({
    queryKey: ['lineitems', 'attention', 'notReady', context, scopeKey],
    queryFn: () =>
      LineItemsApi.queryAll({ where: ['lineItem.executionState in NOT_READY'], orderBy: 'startDate' }),
    enabled,
  })

  const awaiting = useQuery({
    queryKey: ['lineitems', 'attention', 'awaiting', context, scopeKey],
    queryFn: () =>
      LineItemsApi.queryAll({ where: ['lineItem.userState in SUBMITTED,RESERVED'], orderBy: 'startDate' }),
    enabled,
  })

  const lists = useMemo(() => {
    // A paused or stopped line item can still report RUNNING; it is not the
    // ad server falling behind, so it is left out.
    const live = (running.data?.results ?? [])
      .filter((li) => deliveryStatusOf(li) === 'RUNNING')
      .map(withPacing)

    return {
      underDelivering: live
        .filter(isUnderDelivering)
        .sort((a, b) => (a.pacing.gap ?? 0) - (b.pacing.gap ?? 0)),
      endingSoon: live.filter((li) => endsWithin(li, ENDING_SOON_DAYS)),
      notReady: (notReady.data?.results ?? [])
        .filter((li) => deliveryStatusOf(li) === 'NOT_READY' && hasNotEnded(li))
        .map(withPacing),
      awaitingApproval: (awaiting.data?.results ?? []).filter(hasNotEnded).map(withPacing),
    }
  }, [running.data, notReady.data, awaiting.data])

  const tabs = useMemo<AttentionTab<AttentionRow>[]>(() => {
    const name: ColumnDef<AttentionRow, unknown> = {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 160 },
      cell: ({ row }) => (
        <NameCell
          name={row.original.name}
          sub={[row.original.advertiserName, row.original.orderName].filter(Boolean).join(' · ')}
        />
      ),
    }
    const timeline: ColumnDef<AttentionRow, unknown> = {
      id: 'timeline',
      header: t('lineItems.campaignPeriod'),
      meta: { minWidth: 140 },
      enableSorting: false,
      cell: ({ row }) => (
        <CampaignTimeline
          startDate={row.original.startDate}
          endDate={row.original.endDate}
          executionState={row.original.executionState}
          deliveryPct={row.original.deliveryPercentage ?? null}
          elapsedTimePct={row.original.elapsedTimePercentage ?? null}
        />
      ),
    }
    const pacing: ColumnDef<AttentionRow, unknown> = {
      id: 'pacing',
      header: t('advertisingHome.pacing'),
      size: 190,
      accessorFn: (row) => row.pacing.gap ?? 0,
      cell: ({ row }) => <PacingBadge pacing={row.original.pacing} />,
    }
    const date = (field: 'startDate' | 'endDate'): ColumnDef<AttentionRow, unknown> => ({
      accessorKey: field,
      header: t(`common.${field}`),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original[field]} />,
    })
    const deliveryStatus: ColumnDef<AttentionRow, unknown> = {
      id: 'executionState',
      header: t('common.delivery'),
      size: 150,
      accessorFn: (row) => deliveryStatusOf(row),
      cell: ({ row }) => <DeliveryStatusBadge status={deliveryStatusOf(row.original)} />,
    }
    const userState: ColumnDef<AttentionRow, unknown> = {
      accessorKey: 'userState',
      header: t('userStates.label'),
      size: 150,
      cell: ({ row }) => <DeliveryStatusBadge status={row.original.userState} />,
    }

    // If a very large network hit the page cap, the running tabs say so
    // instead of their usual hint.
    const truncated = running.data && running.data.results.length < running.data.totalCount
    const runningHint = (key: string) =>
      truncated
        ? t('advertisingHome.truncated', {
            shown: running.data!.results.length,
            total: running.data!.totalCount,
          })
        : t(`advertisingHome.hints.${key}`, { days: ENDING_SOON_DAYS })

    const tab = (
      key: keyof typeof lists,
      loading: boolean,
      columns: ColumnDef<AttentionRow, unknown>[],
      hint: string = t(`advertisingHome.hints.${key}`),
    ): AttentionTab<AttentionRow> => ({
      key,
      label: t(`advertisingHome.tabs.${key}`),
      hint,
      empty: t(`advertisingHome.empty.${key}`),
      rows: lists[key],
      loading,
      columns,
    })

    return [
      tab('underDelivering', running.isLoading, [name, pacing, timeline, date('endDate')], runningHint('underDelivering')),
      tab('notReady', notReady.isLoading, [name, deliveryStatus, date('startDate'), date('endDate')]),
      tab('endingSoon', running.isLoading, [name, date('endDate'), pacing, timeline], runningHint('endingSoon')),
      tab('awaitingApproval', awaiting.isLoading, [name, userState, date('startDate'), date('endDate')]),
    ]
  }, [t, lists, running.data, running.isLoading, notReady.isLoading, awaiting.isLoading])

  return (
    <AttentionCard
      tabs={tabs}
      storageKey="lineItems"
      onRowClick={(row) => navigate(`/advertising/lineitems/${row.id}`)}
      viewAllHref="/advertising/lineitems"
    />
  )
}
